/**
 * AdminHeader + breadcrumb trail for the admin shell.
 * Kept separate to keep AdminLayout focused on composition.
 */
import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Search } from "lucide-react";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { AdminDatabaseBadge } from "@/components/admin/AdminDatabaseBadge";
import {
  ADMIN_SIDEBAR_NAV_GROUPS,
  isAdminPathBlocked,
  type AdminSidebarNavItem,
} from "@/components/admin/adminSidebarNav";
import { useAdminFeatureAccess } from "@/hooks/useAdminFeatureAccess";
import { resolveAdminDatabaseScope } from "@/lib/adminDatabaseScope";
import { getAdminPanelVersion } from "@/lib/adminPanelVersion";
import { useIsSuperAdmin } from "@/lib/auth";
import { cn } from "@/lib/utils";

type BreadcrumbTrail = {
  section: string;
  page: string;
  sub: string | null;
  hint: string | null;
};

function useBreadcrumbTrail(): BreadcrumbTrail {
  const { pathname, hash } = useLocation();
  const { t } = useTranslation();
  const isSuperAdmin = useIsSuperAdmin();
  const { hiddenSet } = useAdminFeatureAccess();

  return useMemo(() => {
    for (const group of ADMIN_SIDEBAR_NAV_GROUPS) {
      for (const item of group.items) {
        // Skip items that are hidden for this user (regular admin)
        if (!isSuperAdmin && isAdminPathBlocked(item.to, hiddenSet)) continue;

        const isItemMatch = item.end
          ? pathname === item.to
          : pathname === item.to || pathname.startsWith(item.to + "/");
        if (isItemMatch) {
          // Also respect hidden children for sub breadcrumb
          const visibleChildren = (item.children || []).filter(
            (c) => isSuperAdmin || !isAdminPathBlocked(c.to, hiddenSet),
          );
          const child = visibleChildren.find(
            (c) => c.to === `${pathname}${hash}` || c.to === pathname + hash,
          );
          return {
            section: group.label,
            page: item.label,
            sub: child?.label ?? null,
            hint: child ? null : (item.hint ?? null),
          };
        }
      }
    }
    return {
      section: t("adminShell.fallbackSection"),
      page: t("adminShell.fallbackPage"),
      sub: null,
      hint: null,
    };
  }, [pathname, hash, t, isSuperAdmin, hiddenSet]);
}

export function AdminHeader({ onOpenPalette }: { onOpenPalette: () => void }) {
  const trail = useBreadcrumbTrail();
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const dbScope = useMemo(() => resolveAdminDatabaseScope(pathname), [pathname]);
  const panelVersion = useMemo(() => getAdminPanelVersion(), []);

  return (
    <header className="admin-material-chrome sticky top-0 z-30 flex h-12 shrink-0 items-center gap-2 border-b border-white/[0.06] px-3 sm:h-12 sm:px-4 max-sm:h-14 max-sm:pt-[env(safe-area-inset-top,0px)]">
      {/* Sidebar toggle — ≥44px hit area on mobile */}
      <SidebarTrigger className="-ml-0.5 h-10 w-10 rounded-xl text-white/40 transition-colors duration-150 hover:bg-white/[0.07] hover:text-white/80 sm:h-7 sm:w-7 sm:rounded-lg" />
      <Separator orientation="vertical" className="mx-0.5 hidden h-3.5 bg-white/[0.07] sm:block" />

      <div className="min-w-0 flex-1">
        <Breadcrumb className="min-w-0">
          <BreadcrumbList className="flex-nowrap gap-1 sm:gap-1.5">
            <BreadcrumbItem className="hidden sm:inline-flex">
              <span className="text-[12px] text-white/25">{trail.section}</span>
            </BreadcrumbItem>
            <BreadcrumbSeparator className="hidden text-white/15 [&>svg]:h-3 [&>svg]:w-3 sm:flex" />
            <BreadcrumbItem className="min-w-0">
              <BreadcrumbPage className="truncate text-[13px] font-medium text-white/75">
                {trail.page}
              </BreadcrumbPage>
            </BreadcrumbItem>
            {trail.sub && (
              <>
                <BreadcrumbSeparator className="text-white/15 [&>svg]:h-3 [&>svg]:w-3" />
                <BreadcrumbItem className="min-w-0">
                  <BreadcrumbPage className="truncate text-[12px] text-white/30">
                    {trail.sub}
                  </BreadcrumbPage>
                </BreadcrumbItem>
              </>
            )}
          </BreadcrumbList>
        </Breadcrumb>
        {trail.hint && (
          <p className="mt-0.5 truncate text-[11px] text-white/30 max-md:hidden">{trail.hint}</p>
        )}
      </div>

      {/* DB plane + version + language + ⌘K — sticky top so every /admin page shows which Postgres it hits */}
      <div className="ml-auto flex items-center gap-1.5">
        <AdminDatabaseBadge scope={dbScope} size="sm" showService className="shrink-0" />
        <span
          className="hidden max-w-[9.5rem] truncate font-mono text-[10px] tabular-nums text-white/25 sm:inline"
          title={panelVersion.title}
          aria-label={`Versión del panel: ${panelVersion.label}`}
        >
          {panelVersion.shortSha}
          {panelVersion.env && panelVersion.env !== "production" ? (
            <span className="text-white/18"> · {panelVersion.env}</span>
          ) : null}
        </span>
        <LanguageSwitcher variant="dark" className="-mr-1" />
        <Separator orientation="vertical" className="hidden h-3.5 bg-white/[0.07] sm:block" />
        <a
          href="/admin/config-health"
          className="hidden items-center rounded-md border border-white/[0.07] px-1.5 py-0.5 text-[10px] text-white/35 transition hover:border-white/15 hover:text-white/70 sm:flex"
          title="Ver estado de integraciones"
        >
          Health
        </a>
        <Button
          variant="ghost"
          size="sm"
          onClick={onOpenPalette}
          aria-label={t("adminShell.search")}
          className="h-10 gap-1.5 rounded-xl border border-white/[0.08] bg-white/[0.04] px-3 text-white/45 ui-transition duration-150 hover:border-white/[0.13] hover:bg-white/[0.08] hover:text-white/85 sm:h-8"
        >
          <Search className="h-4 w-4 shrink-0 sm:h-3.5 sm:w-3.5" aria-hidden />
          <span className="hidden text-[12px] sm:inline">{t("adminShell.search")}</span>
          <kbd className="hidden items-center gap-px rounded border border-white/[0.09] bg-white/[0.05] px-1.5 font-mono text-[10px] leading-5 tracking-wide text-white/25 sm:flex">
            ⌘K
          </kbd>
        </Button>
      </div>
    </header>
  );
}
